<script setup lang="ts">
import { useConfirm } from "primevue/useconfirm";
import { ProfileAdministrationAPI } from "../../api";
import { useI18n } from "vue-i18n";
import { bringFocusToElementById } from "../../utils/GeneralUtils";

const props = defineProps({
    type: {
        type: String,
        required: true,
        validator: function (value: string) {
            return ["project", "participant", "project-overview"].includes(value);
        },
    },
    id: {
        type: Number,
        required: true,
    },
    directLink: Boolean,
    directInput: Boolean,
    name: {
        type: String,
        required: true,
    },
    message: {
        type: String,
        required: true,
    },
    header: {
        type: String,
        required: true,
    },
    showButton: {
        type: Boolean,
        default: true,
        required: false,
    },
    outlined: {
        type: Boolean,
        default: false,
        required: false,
    },
});

const { t } = useI18n();

const emit = defineEmits(["update:deleteProject", "delete:participant"]);

const confirm = useConfirm();

const deletionConfirm = () => {
    confirm.require({
        group: props.name,
        message: props.message,
        header: props.header,
        accept: async () => {
            if (props.type === "project" || props.type === "project-overview") {
                if (props.id) {
                    // profile-administration has profile-contractor delete the project's direct input and
                    // calculations together with its other data (RDT-470).
                    if (props.directLink) {
                        await ProfileAdministrationAPI.deleteDirectLink(props.id);
                    }
                    await ProfileAdministrationAPI.deleteProject(props.id);
                    emit("update:deleteProject", props.id);
                }
            } else if (props.type === "participant") {
                emit("delete:participant", props.id);
            }
        },
    });
};

/**
 * Accessibility: Brings focus to the delete button in the dialog when it is shown.
 */
function bringFocusToDialog() {
    bringFocusToElementById(`deletion-confirmation_btn_${props.type}-${props.id}-cancel`);
}
</script>

<template>
    <ConfirmDialog
        :group="name"
        :dismissable-mask="true"
        :block-scroll="true"
        :draggable="false"
        :id="`deletion-confirmation_confirm-dialog_${type}-${id}`"
        @show="bringFocusToDialog()"
    >
        <template #container="{ message: dialogMessage, acceptCallback, rejectCallback }">
            <div class="flex flex-col items-center p-8 bg-surface rounded">
                <div
                    class="rounded-full bg-brand text-brand-contrast inline-flex justify-center items-center h-24 w-24 -mt-20"
                >
                    <i class="pi pi-question text-5xl text-white"></i>
                </div>
                <span class="font-bold text-2xl block mb-2 mt-6">{{ dialogMessage.header }}</span>
                <p class="mb-0">{{ dialogMessage.message }}</p>
                <div class="flex items-center gap-2 mt-6">
                    <Button
                        :id="`deletion-confirmation_btn_${type}-${id}-delete`"
                        :label="t('buttons.delete')"
                        @click="acceptCallback"
                    ></Button>
                    <Button :id="`deletion-confirmation_btn_${type}-${id}-cancel`" :label="t('buttons.cancel')" outlined @click="rejectCallback"></Button>
                </div>
            </div>
        </template>
    </ConfirmDialog>
    <Button
        :id="`deletion-confirmation_btn_${type}-${id}`"
        :text="!outlined"
        :outlined="outlined"
        severity="danger"
        icon="pi pi-trash"
        v-tooltip="t(`buttons.delete${type === 'project-overview' ? 'Project' : type === 'participant' ? 'Participant' : ''}`)"
        @click="deletionConfirm()"
    ></Button>
</template>

<style>
#deletionConfirmationDialog {
    max-width: 60%;
    align-self: center;
}
</style>
